import { useQuery } from "@tanstack/react-query";

import { IHospital, ITenant } from "../../../@types/admin/AdminHospital";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { hospitalsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  mergeTenantStyles,
  parseTenantStyles,
} from "../../../utils/tenantStyles";

export function useHospitals() {
  const { data, isLoading, isError, isFetching } = useQuery<
    IPaginatedResponse<IHospital[]>
  >({
    queryKey: ["admin-hospitals"],
    queryFn: async () => ensureOk(await hospitalsService.getHospitals()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { hospitals: data?.result ?? [], isLoading, isError, isFetching };
}

/**
 * Tenant catalogue. The hospital list stores a tenant id only, so this names
 * the Tenant column — the same join Retool did in a `valueOverride`.
 */
export function useTenants() {
  const { data, isLoading, isError } = useQuery<IPaginatedResponse<ITenant[]>>({
    queryKey: ["admin-tenants"],
    queryFn: async () => ensureOk(await hospitalsService.getTenants()),
    // Feeds the Tenants page as well as the hospital list's Tenant column, so
    // it follows the same no-Refresh-button rule as the other list queries.
    refetchOnMount: "always",
  });

  return { tenants: data?.result ?? [], isLoading, isError };
}

export function useTenantDetails(tenantId: string | undefined) {
  const { data, isLoading, isError } = useQuery<ITenant>({
    queryKey: ["admin-tenant-details", tenantId],
    queryFn: async () =>
      ensureOk(await hospitalsService.getTenantById(tenantId!)),
    enabled: Boolean(tenantId),
    refetchOnMount: "always",
  });

  return { tenant: data, isLoading, isError };
}

/**
 * A tenant's theme, already merged over the shipped defaults — the portal
 * port of Retool's `HandleStyles` chain. A 204 (tenant never themed) resolves
 * to the defaults rather than an error.
 */
export function useTenantStyles(tenantId: string | undefined) {
  const { data, isLoading, isError } = useQuery<Record<string, string>>({
    queryKey: ["admin-tenant-styles", tenantId],
    queryFn: async () => {
      const response = await hospitalsService.getTenantStyles(tenantId!);
      const payload = ensureOk(response);
      return mergeTenantStyles(parseTenantStyles(payload?.tenantStyles));
    },
    enabled: Boolean(tenantId),
    refetchOnMount: "always",
  });

  return { styles: data, isLoading, isError };
}
